import { useQuery } from '@tanstack/react-query'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@workspace/ui/components/card'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { FullScreenState } from '@workspace/ui/components/full-screen-state'
import { useEffect, useState } from 'react'
import { useCurrentUserQuery, useMyMembershipsQuery } from '../../../hooks/api'
import { auth } from '../../../lib/auth'
import { trackEvent } from '../../../lib/gtag'
import { fetchClientName, readOAuthQuery, submitConsent } from '../lib/oauth-api'

/**
 * The investor decides whether an assistant may act as them. The assistant's name is whatever it
 * called itself when it registered, so it is shown as plain text next to the host it will return
 * to, and only investor accounts can approve a connection.
 */
export function OAuthConsent() {
  const { oauthQuery, clientId, redirectHost } = readOAuthQuery()
  const { data: user, isPending: userPending } = useCurrentUserQuery()
  const { data: clientName, isPending: clientPending } = useQuery({
    queryKey: ['oauth-client-name', clientId],
    queryFn: () => (clientId ? fetchClientName(clientId) : Promise.resolve(null)),
    enabled: !!clientId,
  })
  const { data: memberships } = useMyMembershipsQuery()
  const investorId = memberships?.find((m) => m.type === 'investor')?.id
  const [switching, setSwitching] = useState(false)
  const isInvestor = user?.userType === 'INVESTOR'
  useEffect(() => {
    if (isInvestor) trackEvent('oauth_consent_viewed')
  }, [isInvestor])
  const [busy, setBusy] = useState<'accept' | 'deny' | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Signs this account out and returns to the assistant's login with the same signed request.
  const switchAccount = async () => {
    trackEvent('oauth_account_switch_clicked', { step: 'consent' })
    setSwitching(true)
    await auth.signOut()
    window.location.href = `/app/oauth/login?${oauthQuery}`
  }

  if (!clientId || !oauthQuery) {
    return (
      <FullScreenState
        title="This link is not valid"
        subtitle="Start again from your AI assistant."
      />
    )
  }
  if (userPending || clientPending) return <FullScreenState loading title="Loading..." />
  if (user?.userType !== 'INVESTOR') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>This connection is for investor accounts</CardTitle>
            <CardDescription>
              {user?.email
                ? `You are signed in as ${user.email}, which is not an investor account.`
                : null}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CtaButton className="w-full" loading={switching} onClick={switchAccount}>
              Sign in with a different account
            </CtaButton>
          </CardContent>
        </Card>
      </div>
    )
  }

  const decide = async (accept: boolean) => {
    trackEvent('oauth_consent_decided', { decision: accept ? 'connect' : 'cancel' })
    setBusy(accept ? 'accept' : 'deny')
    setError(null)
    try {
      window.location.href = await submitConsent(accept, oauthQuery)
    } catch {
      setBusy(null)
      setError('Something went wrong. Start again from your AI assistant.')
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Connect {clientName ?? 'an AI assistant'} to SeedForge?</CardTitle>
          <CardDescription>
            {redirectHost ? `After you decide, you return to ${redirectHost}.` : null}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">It will be able to</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                search startups and read what founders have published, as you can in SeedForge
              </li>
              <li>
                save its read of a startup to your account, privately and without following it, when
                you ask it for its view
              </li>
              <li>
                keep your follows, notes and thesis for you, and tell you when something changes
              </li>
            </ul>
            <p className="font-medium text-foreground">It will not</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>see startups that are in stealth or any scores or judgments about a startup</li>
              <li>send anything to founders, or show them what you asked</li>
            </ul>
            <p>Founders see that you follow. Your condition stays private.</p>
            <p>You can disconnect it at any time from your SeedForge account.</p>
          </div>
          {investorId && (
            <p className="text-sm text-muted-foreground">
              Want to know more?{' '}
              <a
                className="underline"
                href={`/app/investor/${investorId}/connect-ai`}
                target="_blank"
                rel="noreferrer"
              >
                What your assistant can do
              </a>{' '}
              and{' '}
              <a
                className="underline"
                href={`/app/investor/${investorId}/settings`}
                target="_blank"
                rel="noreferrer"
              >
                how your data is handled
              </a>
              .
            </p>
          )}
          <div className="flex gap-2">
            <CtaButton
              className="flex-1"
              loading={busy === 'accept'}
              disabled={busy !== null}
              onClick={() => decide(true)}
            >
              Connect
            </CtaButton>
            <SecondaryButton
              className="flex-1"
              disabled={busy !== null}
              onClick={() => decide(false)}
            >
              Cancel
            </SecondaryButton>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <p className="text-xs text-muted-foreground">
            Signed in as {user.email}.{' '}
            <button
              type="button"
              className="underline"
              disabled={switching || busy !== null}
              onClick={switchAccount}
            >
              Not you? Sign out
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
